import type { FC } from 'react';
import React, { useRef, useState, useEffect, startTransition } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { AudioButtonLoaderActivatable, AudioButtonLoaderAfterPaint } from './AudioButtonLoader.tsx';

type AudioButtonLoaderWrapperProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
	isDisabled?: boolean;
	isView: boolean;
};

/**
 *
 * Wrapper component for AudioButton.tsx to selectively hydrate (defer hydration) until user action (hover)
 *
 */
export const AudioButtonLoaderWrapper: FC<AudioButtonLoaderWrapperProps> = (props) => {
	const { isView, contentId, contentType, spaceKey, contentTitle, isDisabled } = props;

	if (isView && expVal('confluence_selective_hydration_audio_button', 'isEnabled', false)) {
		return <AudioButtonSelectiveHydrationWrapper {...props} />;
	}

	// functionality is unchanged, AudioButton is only wrapped in a Loadable
	return (
		<AudioButtonLoaderAfterPaint
			contentId={contentId}
			contentType={contentType}
			spaceKey={spaceKey}
			contentTitle={contentTitle}
			isDisabled={isDisabled}
		/>
	);
};

/**
 * Internal component used only when selective hydration is enabled.
 * Contains all SH-specific logic (browser support, keyboard focus unlock).
 *
 * JS for AudioButton is delayed until user hovers (currently for View Page and Space Overviews)
 * Cases for immediate unlock (bypassing selective hydration) include:
 *
 * Disabled browser (Firefox)
 * User activation via keyboard (tab)
 *
 */
const AudioButtonSelectiveHydrationWrapper: FC<AudioButtonLoaderWrapperProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	isDisabled,
}) => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	// Assume audio is enabled for SSR until client-side useEffect checks browser support
	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState<boolean | null>(true);
	const [isFocusActivated, setIsFocusActivated] = useState(false);

	const renderServerPlaceholder = useRenderServerPlaceholder();

	useEffect(() => {
		// Only check audio support when selective hydration is enabled.
		// For FG-OFF we rely on the browser check within AudioButton.
		const isAudioSupportedByBrowser =
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

		if (!isAudioSupportedByBrowser) {
			startTransition(() => {
				setAudioSupportedByBrowser(false);
			});
		}
	}, []);

	useEffect(() => {
		// Focus listener to determine if a user has tabbed to the button via keyboard, in which case
		// the component is immediately hydrated. This listener must live outside AudioButton code and hydrate if button is activated.

		const wrapper = wrapperRef.current;
		if (!wrapper) {
			return;
		}

		const handleFocus = (event: FocusEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || !target.matches('button, [role="button"]')) {
				return;
			}
			const isKeyboardFocus = target.matches(':focus-visible');
			if (!isKeyboardFocus) {
				return;
			}
			startTransition(() => {
				setIsFocusActivated(true);
			});
		};

		wrapper.addEventListener('focus', handleFocus, true);
		return () => {
			wrapper.removeEventListener('focus', handleFocus, true);
		};
	}, []);

	// Only immediately hydrate if:
	// 1) audio button is not supported by browser (firefox) and component needs to hydrate to show as disabled on page load
	// 2) user activates the button via keyboard focus
	const immediateActivationCondition = audioSupportedByBrowser === false || isFocusActivated;

	const isButtonDisabled = isDisabled || (!renderServerPlaceholder && !audioSupportedByBrowser);

	const wrapperProps = {
		audioSupportedByBrowserFromWrapper: audioSupportedByBrowser ?? true,
	};

	return (
		<div ref={wrapperRef}>
			<AudioButtonLoaderActivatable
				contentId={contentId}
				contentType={contentType}
				spaceKey={spaceKey}
				contentTitle={contentTitle}
				isDisabled={isButtonDisabled}
				immediateActivationCondition={immediateActivationCondition}
				selectiveHydrationEnabled
				{...wrapperProps}
			/>
		</div>
	);
};
